import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowsPointingOutIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import {
  AdManagementService,
  type DetectionResult,
  type GeneratedAdResponse,
  type ReferenceImage,
} from '../../client'
import Modal from '../ui/Modal'
import MediaPreview from '../home/MediaPreview'
import EditComposer from './EditComposer'
import ImageSourcePopover from './ImageSourcePopover'

type ChatMessage = {
  role: 'user' | 'assistant'
  text: string
  loading?: boolean
  fromVersion?: number
  detection?: DetectionResult
  imageUrls?: string[]
}

type Rect = { left: number; top: number; width: number; height: number }

type Props = {
  brandId: number
  ads: GeneratedAdResponse[]
  adIndices: number[]
  creditsRemaining: number
  creditsLimit: number
  referenceUrls?: (string | null)[]
  onClose: () => void
  onContinue: (ads: GeneratedAdResponse[]) => void
}

function initialVersions(ads: GeneratedAdResponse[], referenceUrls?: (string | null)[]) {
  return ads.map((ad, index) => {
    const base = ad.image_versions?.length ? [...ad.image_versions] : ad.image_url ? [ad.image_url] : []
    const reference = referenceUrls?.[index]
    return reference ? [reference, ...base] : base
  })
}

/** Aligns per-version detections with image_versions, prepending an empty slot for the reference. */
function initialDetections(ads: GeneratedAdResponse[], referenceUrls?: (string | null)[]) {
  return ads.map((ad, index) => {
    let base: DetectionResult[][]

    if (ad.version_detections?.length) {
      base = [...ad.version_detections]
    } else {
      const length = ad.image_versions?.length ?? (ad.image_url ? 1 : 0)
      base = Array.from({ length }, () => [] as DetectionResult[])
      if (length > 0) base[length - 1] = ad.detections ?? []
    }

    return referenceUrls?.[index] ? [[], ...base] : base
  })
}

export default function AdChatReview({
  brandId,
  ads,
  adIndices,
  creditsRemaining,
  creditsLimit,
  referenceUrls,
  onClose,
  onContinue,
}: Props) {
  const [versions, setVersions] = useState(() => initialVersions(ads, referenceUrls))
  const [detections, setDetections] = useState(() => initialDetections(ads, referenceUrls))
  const [versionIndices, setVersionIndices] = useState<number[]>(() =>
    ads.map((_, index) => (referenceUrls?.[index] ? 1 : 0)),
  )
  const [threads, setThreads] = useState<Record<number, ChatMessage[]>>(() =>
    Object.fromEntries(
      ads.flatMap((ad, index) =>
        ad.chat_history?.length
          ? [[index, ad.chat_history.map(({ role, text }) => ({ role, text }) as ChatMessage)]]
          : [],
      ),
    ),
  )
  const [current, setCurrent] = useState(0)
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [credits, setCredits] = useState(creditsRemaining)
  const [showBoxes, setShowBoxes] = useState(false)
  const [imageRect, setImageRect] = useState<Rect | null>(null)
  const [hovered, setHovered] = useState<number | null>(null)
  const [selected, setSelected] = useState<DetectionResult | null>(null)
  const [popup, setPopup] = useState<{ x: number; y: number } | null>(null)
  const [popupInput, setPopupInput] = useState('')
  const [lightbox, setLightbox] = useState<string | null>(null)
  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([])
  const [pendingUrls, setPendingUrls] = useState<string[]>([])
  const [attachment, setAttachment] = useState<{ anchor: DOMRect; theme: 'light' | 'dark' } | null>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const chatRef = useRef<HTMLDivElement>(null)

  const hasReference = (index: number) => Boolean(referenceUrls?.[index])
  const isReferenceVersion = (index: number, version: number) => hasReference(index) && version === 0
  const versionLabel = (index: number, version: number) =>
    isReferenceVersion(index, version) ? 'Ref' : `v${version + (hasReference(index) ? 0 : 1)}`

  const currentVersions = versions[current] ?? []
  const currentVersion = versionIndices[current] ?? 0
  const currentImage = currentVersions[currentVersion] ?? ''
  const currentDetections = detections[current]?.[currentVersion] ?? []
  const thread = threads[current] ?? []
  const viewingReference = isReferenceVersion(current, currentVersion)
  // every ad needs at least one generated version beyond its reference
  const canContinue = ads.every((_, index) => (versions[index] ?? []).length > (hasReference(index) ? 1 : 0))

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight })
  }, [thread.length, sending])

  useEffect(() => {
    if (!popup) return

    function onPointerDown(event: MouseEvent) {
      const target = event.target as HTMLElement
      // the image area toggles its own selection; the attach popover lives outside this popup
      if (containerRef.current?.contains(target) || target.closest?.('[data-detection-popup],[data-image-source]')) return
      setSelected(null)
      setPopup(null)
    }

    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [popup])

  const measureImage = useCallback(() => {
    const image = imageRef.current
    const container = containerRef.current
    if (!image || !container) return

    const bounds = image.getBoundingClientRect()
    const box = container.getBoundingClientRect()
    setImageRect({
      left: bounds.left - box.left,
      top: bounds.top - box.top,
      width: bounds.width,
      height: bounds.height,
    })
  }, [])

  useEffect(() => {
    const image = imageRef.current
    if (!image) return

    const observer = new ResizeObserver(measureImage)
    observer.observe(image)
    return () => observer.disconnect()
  }, [measureImage, currentImage])

  useEffect(() => {
    AdManagementService.getAdGenerationDoc(brandId)
      .then((doc) => setReferenceImages(doc?.reference_images ?? []))
      .catch(() => undefined)
  }, [brandId])

  const attachments = pendingUrls.length > 0 && (
    <div className="flex flex-wrap items-center gap-2 px-1">
      {pendingUrls.map((url) => (
        <div key={url} className="group relative size-12 shrink-0">
          <span className="block size-full overflow-hidden rounded-lg border border-gray-200">
            <img src={url} alt="Reference" className="size-full object-cover" />
          </span>
          <button
            type="button"
            aria-label="Preview image"
            onClick={() => setLightbox(url)}
            className="absolute inset-0 flex items-center justify-center rounded-lg transition group-hover:bg-black/30"
          >
            <ArrowsPointingOutIcon className="size-3.5 text-white opacity-0 transition group-hover:opacity-100" />
          </button>
          <button
            type="button"
            aria-label="Remove image"
            onClick={() => setPendingUrls((all) => all.filter((item) => item !== url))}
            className="absolute -top-1 -right-1 z-10 flex size-4 items-center justify-center rounded-full bg-gray-700 text-white shadow hover:bg-gray-900"
          >
            <XMarkIcon className="size-2.5" />
          </button>
        </div>
      ))}
    </div>
  )

  function clearOverlay() {
    setImageRect(null)
    setHovered(null)
    setSelected(null)
    setPopup(null)
  }

  function selectAd(index: number) {
    clearOverlay()
    setCurrent(index)
  }

  function selectVersion(index: number) {
    clearOverlay()
    setVersionIndices((all) => all.map((value, at) => (at === current ? index : value)))
  }

  function onOverlayMove(event: React.MouseEvent<HTMLDivElement>) {
    const bounds = event.currentTarget.getBoundingClientRect()
    const x = ((event.clientX - bounds.left) / bounds.width) * 1000
    const y = ((event.clientY - bounds.top) / bounds.height) * 1000

    let best: number | null = null
    let smallest = Infinity

    currentDetections.forEach(({ box: [y0, x0, y1, x1] }, index) => {
      if (x >= x0 && x <= x1 && y >= y0 && y <= y1) {
        const area = (x1 - x0) * (y1 - y0)
        if (area < smallest) {
          smallest = area
          best = index
        }
      }
    })

    setHovered(best)
  }

  function onOverlayClick(event: React.MouseEvent<HTMLDivElement>) {
    if (hovered === null) return

    const detection = currentDetections[hovered]
    if (!detection) return
    if (selected === detection) {
      setSelected(null)
      setPopup(null)
      return
    }

    const bounds = event.currentTarget.getBoundingClientRect()
    const [, x0, y1] = detection.box

    setSelected(detection)
    setPopup({
      x: bounds.left + (x0 / 1000) * bounds.width,
      y: bounds.top + (y1 / 1000) * bounds.height + 8,
    })
  }

  async function send(text: string, detection: DetectionResult | null) {
    if (!text.trim() || sending || credits <= 0 || viewingReference) return

    const index = current
    const attachments = pendingUrls.length ? [...pendingUrls] : undefined
    const message: ChatMessage = {
      role: 'user',
      text,
      fromVersion: currentVersion + 1,
      detection: detection ?? undefined,
      imageUrls: attachments,
    }

    setThreads((all) => ({ ...all, [index]: [...(all[index] ?? []), message, { role: 'assistant', text: '', loading: true }] }))
    setPendingUrls([])
    setPopup(null)
    setSending(true)

    try {
      const data = await AdManagementService.chatEditImage(brandId, {
        image_url: currentImage || ads[index]?.image_url || '',
        instruction: text,
        ad_index: adIndices[index],
        bounding_box: detection?.box ?? null,
        detection_label: detection?.label ?? null,
        reference_image_urls: attachments ?? [],
      })

      setVersions((all) => all.map((list, at) => (at === index ? [...list, data.new_image_url] : list)))
      setDetections((all) => all.map((list, at) => (at === index ? [...list, data.detections ?? []] : list)))
      // pre-append length is the index of the version just appended
      setVersionIndices((all) => all.map((value, at) => (at === index ? (versions[index] ?? []).length : value)))
      setCredits(data.credits_remaining)
      setThreads((all) => ({
        ...all,
        [index]: (all[index] ?? []).map((item, at, list) =>
          at === list.length - 1 ? { ...item, loading: false } : item,
        ),
      }))
    } catch {
      setThreads((all) => ({
        ...all,
        [index]: (all[index] ?? []).map((item, at, list) =>
          at === list.length - 1 ? { role: 'assistant', text: 'Something went wrong. Please try again.' } : item,
        ),
      }))
    } finally {
      setSending(false)
      setSelected(null)
    }
  }

  function handleContinue() {
    onContinue(
      ads.map((ad, index) => {
        const list = versions[index] ?? []
        let at = versionIndices[index] ?? 0
        if (isReferenceVersion(index, at)) at = Math.min(1, list.length - 1)
        return { ...ad, image_url: list[at] || ad.image_url }
      }),
    )
  }

  return (
    <>
      <Modal
        width="max-w-5xl"
        padded={false}
        onClose={onClose}
        title={
          <>
            Edit Ad Image
            <span
              data-tooltip="Hover over the image to select a specific object, or describe your edit in the chat."
              className="inline-flex"
            >
              <InformationCircleIcon className="size-4 text-gray-400" />
            </span>
          </>
        }
        headerRight={
          <span className="text-xs text-gray-400">
            {creditsLimit - credits} / {creditsLimit} credits used
          </span>
        }
        footer={
          <div className="flex justify-end">
            <button
              type="button"
              disabled={!canContinue}
              onClick={handleContinue}
              className="rounded bg-purple-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-purple-950 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-purple-900"
            >
              Generate {ads.length} Ad{ads.length === 1 ? '' : 's'}
            </button>
          </div>
        }
      >
        <div className="flex min-h-0 flex-1 flex-col-reverse overflow-hidden md:flex-row">
          <div className="flex min-h-0 basis-1/2 flex-col border-t border-gray-100 md:w-2/5 md:basis-auto md:border-t-0 md:border-r">
            <div ref={chatRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
              {thread.length === 0 ? (
                <div className="flex h-full items-center justify-center">
                  <p className="px-4 text-center text-sm text-gray-400">
                    Describe changes you'd like to make to this ad image. Each edit uses one credit.
                  </p>
                </div>
              ) : (
                thread.map((message, position) =>
                  message.role === 'user' ? (
                    <div key={position} className="flex justify-end">
                      <div className="flex max-w-[85%] flex-col items-end gap-0.5">
                        {message.detection && (
                          <span className="self-end rounded-full bg-purple-200/60 px-2 py-0.5 text-[10px] font-medium text-purple-700">
                            {message.detection.label}
                          </span>
                        )}
                        {message.imageUrls && message.imageUrls.length > 0 && (
                          <div className="flex flex-wrap justify-end gap-1">
                            {message.imageUrls.map((url) => (
                              <button
                                key={url}
                                type="button"
                                aria-label="Preview image"
                                onClick={() => setLightbox(url)}
                                className="size-16 overflow-hidden rounded-lg border border-purple-200"
                              >
                                <img src={url} alt="Reference" className="size-full object-cover" />
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="rounded-xl rounded-tr-sm bg-purple-100 px-3 py-2 text-sm text-purple-900">
                          {message.text}
                        </div>
                        {message.fromVersion && (
                          <span className="text-[10px] text-gray-400">editing v{message.fromVersion}</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div key={position} className="flex justify-start">
                      <div className="flex items-center gap-2 rounded-xl rounded-tl-sm bg-gray-100 px-3 py-2 text-sm text-gray-700">
                        {message.loading ? (
                          <>
                            <span className="size-3.5 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500" />
                            Updating...
                          </>
                        ) : (
                          message.text || 'Updated.'
                        )}
                      </div>
                    </div>
                  ),
                )
              )}
            </div>

            <div className="shrink-0 p-3">
              {viewingReference ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-center">
                  <p className="text-xs text-slate-500">
                    Reference ad — select <span className="font-semibold text-slate-700">v1</span> to edit your
                    generated version.
                  </p>
                </div>
              ) : credits <= 0 ? (
                <p className="py-2 text-center text-xs text-gray-400">No credits remaining</p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {!popup && attachments}
                  <EditComposer
                    theme="light"
                    value={input}
                    rows={1}
                    disabled={sending}
                    onChange={setInput}
                    onSend={() => {
                      void send(input, selected)
                      setInput('')
                    }}
                    onAttach={(anchor) => setAttachment({ anchor, theme: 'light' })}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex min-h-0 basis-1/2 flex-col gap-3 p-4 md:w-3/5 md:basis-auto">
            <div
              ref={containerRef}
              onMouseEnter={() => setShowBoxes(true)}
              onMouseLeave={() => setShowBoxes(false)}
              className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-gray-100"
            >
              {currentImage ? (
                <img
                  ref={imageRef}
                  src={currentImage}
                  onLoad={measureImage}
                  alt={`Generated ad ${current + 1}`}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <span className="text-sm text-gray-400">No image available</span>
              )}

              {imageRect && currentDetections.length > 0 && (showBoxes || sending || selected) && (
                <div
                  onMouseMove={onOverlayMove}
                  onMouseLeave={() => setHovered(null)}
                  onClick={onOverlayClick}
                  style={imageRect}
                  className={`absolute ${sending ? 'pointer-events-none' : 'cursor-crosshair'}`}
                >
                  {currentDetections.map((detection, position) => {
                    const isSelected = selected === detection
                    if (!isSelected && (sending || hovered !== position)) return null

                    const [y0, x0, y1, x1] = detection.box
                    return (
                      <div
                        key={`${detection.label}-${position}`}
                        style={{
                          left: `${x0 / 10}%`,
                          top: `${y0 / 10}%`,
                          width: `${(x1 - x0) / 10}%`,
                          height: `${(y1 - y0) / 10}%`,
                        }}
                        className={`pointer-events-none absolute overflow-hidden rounded-lg border-2 transition-all duration-300 ${
                          isSelected ? 'border-purple-400' : 'border-white'
                        } ${sending && isSelected ? 'bg-white/20 backdrop-blur-sm' : ''}`}
                      >
                        {sending && isSelected && <span className="box-shimmer" />}
                      </div>
                    )
                  })}
                </div>
              )}

              {ads.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Previous ad"
                    disabled={current === 0 || sending}
                    onClick={() => selectAd(current - 1)}
                    className="absolute top-1/2 left-2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <ChevronLeftIcon className="size-5 text-gray-700" />
                  </button>
                  <button
                    type="button"
                    aria-label="Next ad"
                    disabled={current === ads.length - 1 || sending}
                    onClick={() => selectAd(current + 1)}
                    className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 shadow transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    <ChevronRightIcon className="size-5 text-gray-700" />
                  </button>
                </>
              )}

              {currentVersions.length > 1 && (
                <div className="absolute right-4 bottom-2 left-4 flex justify-center">
                  <div className="flex max-w-full items-center gap-1 overflow-x-auto rounded-full bg-black/25 px-1.5 py-1 shadow-lg ring-1 ring-white/20 backdrop-blur-sm [scrollbar-width:none]">
                    {currentVersions.map((_, position) => (
                      <button
                        key={position}
                        type="button"
                        disabled={sending}
                        onClick={() => selectVersion(position)}
                        className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                          currentVersion === position
                            ? isReferenceVersion(current, position)
                              ? 'bg-slate-200 text-slate-800 shadow-sm'
                              : 'bg-white text-gray-900 shadow-sm'
                            : 'text-white/80 hover:bg-white/20 hover:text-white'
                        }`}
                      >
                        {versionLabel(current, position)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {ads.length > 1 && (
              <div className="flex shrink-0 items-center justify-center gap-2 overflow-x-auto pt-1 pb-0.5 [scrollbar-width:none]">
                {ads.map((_, position) => {
                  const list = versions[position] ?? []
                  const thumb = list[versionIndices[position] ?? 0] ?? ''
                  return (
                    <button
                      key={position}
                      type="button"
                      disabled={sending}
                      onClick={() => selectAd(position)}
                      className={`shrink-0 ${sending ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                      <span
                        className={`block size-14 overflow-hidden rounded-lg ${
                          current === position ? 'outline-2 outline-offset-2 outline-purple-600' : ''
                        }`}
                      >
                        {thumb ? (
                          <img src={thumb} alt="" className="size-full object-cover" />
                        ) : (
                          <span className="flex size-full items-center justify-center bg-gray-100">
                            <PhotoIcon className="size-5 text-gray-400" />
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block w-14 text-center text-[10px] text-gray-400">
                        Ad {position + 1}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {popup &&
        selected &&
        createPortal(
          <div data-detection-popup style={{ left: popup.x, top: popup.y }} className="fixed z-[200] w-56">
            <div className="relative rounded-xl border border-white/10 bg-gray-900/80 px-2.5 pt-2 pb-8 shadow-2xl backdrop-blur-md">
              {attachments && <div className="mb-1.5">{attachments}</div>}
              <span className="pointer-events-none absolute right-[60px] bottom-1.5 left-2.5 truncate text-[10px] leading-tight text-white/40">
                {selected.label.replace(/\b\w/g, (letter) => letter.toUpperCase())}
              </span>
              <button
                type="button"
                aria-label="Close"
                onClick={() => {
                  setSelected(null)
                  setPopup(null)
                }}
                className="absolute top-1.5 right-1.5 text-white/40 transition hover:text-white/80"
              >
                <XMarkIcon className="size-3.5" />
              </button>
              <EditComposer
                theme="dark"
                autoFocus
                value={popupInput}
                rows={2}
                disabled={sending}
                onChange={setPopupInput}
                onSend={() => {
                  void send(popupInput, selected)
                  setPopupInput('')
                }}
                onAttach={(anchor) => setAttachment({ anchor, theme: 'dark' })}
                onEscape={() => {
                  setSelected(null)
                  setPopup(null)
                }}
              />
            </div>
          </div>,
          document.body,
        )}

      {attachment && (
        <ImageSourcePopover
          brandId={brandId}
          anchor={attachment.anchor}
          theme={attachment.theme}
          images={referenceImages}
          selected={pendingUrls}
          onImagesChange={setReferenceImages}
          onSelectedChange={setPendingUrls}
          onPreview={setLightbox}
          onClose={() => setAttachment(null)}
        />
      )}

      <MediaPreview src={lightbox} alt="Full size preview" onClose={() => setLightbox(null)} />
    </>
  )
}
